/**
 * What the platform worked out from the customer's last message.
 *
 * Each row is one condition it resolved. Opening a row shows the reasoning it
 * recorded and which outcome capabilities depend on that condition, which is
 * what makes the answer matter to the agent rather than just being a fact.
 */
import { useState } from "react";
import { Outcome } from "@financial-advisory/data-access/api-types";
import { Check, ChevronRight, X } from "lucide-react";
import { cn, normaliseString } from "@unlikelyai-magic/utils";
import type { ChangedNode } from "@unlikelyai-magic/utils";

/**
 * True when this is the question the platform asked the agent to chase, so the
 * row can show that the last reply did its job.
 */
const wasAskedFor = (question: string, targetQuestions?: string[]): boolean =>
  (targetQuestions ?? []).some(
    (target) => normaliseString(target) === normaliseString(question)
  );

const ChangedNodeRow = ({
  node,
  askedFor,
}: {
  node: ChangedNode;
  askedFor: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const usedIn = Array.from(node.usedIn ?? []);
  const canOpen = Boolean(node.reasoning) || usedIn.length > 0;
  const isTrue = node.value === Outcome.TRUE;
  const Icon = isTrue ? Check : X;

  const row = (
    <>
      {canOpen && (
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 flex-none text-brand-muted transition-transform duration-200 motion-reduce:transition-none",
            isOpen && "rotate-90"
          )}
          aria-hidden={true}
        />
      )}
      <Icon
        className={cn(
          "h-3.5 w-3.5 flex-none stroke-[3]",
          isTrue ? "text-green-600" : "text-red-600"
        )}
        aria-hidden={true}
      />
      <span className="min-w-0 flex-1 text-xs">{node.question}</span>
      {askedFor && (
        <span className="flex-none rounded-full border border-unlikely/30 bg-unlikely/[0.09] px-1.5 py-px text-[10px] font-semibold text-unlikely">
          we asked for this
        </span>
      )}
      <span
        className={cn(
          "flex-none font-mono text-[10px] font-semibold",
          isTrue ? "text-green-600" : "text-red-600"
        )}
      >
        {isTrue ? "TRUE" : "FALSE"}
      </span>
    </>
  );

  const rowClassName = cn(
    "flex w-full items-center gap-2 px-2.5 py-1.5 text-left",
    isTrue ? "bg-green-500/[0.05]" : "bg-red-500/[0.04]"
  );

  return (
    <li
      className={cn(
        "overflow-hidden rounded-md border",
        isTrue ? "border-green-500/30" : "border-red-500/25"
      )}
    >
      {canOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          className={cn(
            rowClassName,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-unlikely/40"
          )}
        >
          {row}
        </button>
      ) : (
        <div className={rowClassName}>{row}</div>
      )}

      {isOpen && canOpen && (
        <div className="flex flex-col gap-2 border-t border-gray-100 bg-brand-card px-2.5 py-2">
          {node.reasoning && (
            <p className="text-[11px] leading-relaxed text-brand-muted">
              {node.reasoning}
            </p>
          )}
          {usedIn.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
                Decides
              </span>
              {usedIn.map((name) => (
                <span
                  key={name}
                  className="rounded-full border border-brand-border bg-brand-background px-2 py-0.5 text-[11px] text-brand-muted"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export const ChangedNodesList = ({
  nodes,
  targetQuestions,
}: {
  nodes: ChangedNode[];
  targetQuestions?: string[];
}) => {
  if (nodes.length === 0) {
    return (
      <p className="text-[13px] text-brand-muted">
        Nothing new was settled by this message.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {nodes.map((node) => (
        <ChangedNodeRow
          key={node.question}
          node={node}
          askedFor={wasAskedFor(node.question, targetQuestions)}
        />
      ))}
    </ul>
  );
};
